import type {
  MemoryColorField,
  MemoryGraphPayload,
  MemoryLinkType,
  MemoryNetworkData,
  MemoryRow,
} from './types'

export function normalizeLinkType(type?: string, lineStyle?: string): MemoryLinkType {
  if (type === 'temporal' || lineStyle === 'dashed') return 'temporal'
  if (type === 'entity') return 'entity'
  if (type === 'causal' || type === 'causes' || type === 'caused_by' || type === 'enables' || type === 'prevents') return 'causal'
  return 'semantic'
}

export function toNetworkData(graph: MemoryGraphPayload): MemoryNetworkData {
  const rows = new Map(graph.table_rows.map((row) => [row.id, row]))
  const nodes = graph.nodes.map(({ data }) => {
    const row = rows.get(data.id)
    const sourceLabel = data.label || row?.text || data.id.slice(0, 8)
    const label = sourceLabel.length > 44 ? `${sourceLabel.slice(0, 44)}…` : sourceLabel
    return { id: data.id, label, color: data.color || '', row }
  })
  const nodeIds = new Set(nodes.map((node) => node.id))
  const links = graph.edges.filter(({ data }) => nodeIds.has(data.source) && nodeIds.has(data.target)).map(({ data }) => {
    const type = normalizeLinkType(data.linkType, data.lineStyle)
    return {
      source: data.source,
      target: data.target,
      type,
      color: data.color || '',
      entity: data.entityName,
      weight: data.weight ?? data.similarity,
    }
  })

  return { nodes, links }
}

export function mergeMemoryGraphs(base: MemoryGraphPayload, focused: MemoryGraphPayload): MemoryGraphPayload {
  const nodes = new Map(base.nodes.map((node) => [node.data.id, node]))
  focused.nodes.forEach((node) => nodes.set(node.data.id, node))

  const rows = new Map(base.table_rows.map((row) => [row.id, row]))
  focused.table_rows.forEach((row) => rows.set(row.id, row))

  const edges = new Map(base.edges.map((edge) => [edgeKey(edge), edge]))
  focused.edges.forEach((edge) => edges.set(edgeKey(edge), edge))

  return {
    ...base,
    nodes: Array.from(nodes.values()),
    edges: Array.from(edges.values()),
    table_rows: Array.from(rows.values()),
    total_units: Math.max(base.total_units, focused.total_units),
    total_links: Math.max(base.total_links, focused.total_links),
  }
}

export function appendMemoryRows(graph: MemoryGraphPayload, additions: MemoryRow[]): MemoryGraphPayload {
  if (additions.length === 0) return graph

  const nodes = new Map(graph.nodes.map((node) => [node.data.id, node]))
  const rows = new Map(graph.table_rows.map((row) => [row.id, row]))
  additions.forEach((row) => {
    rows.set(row.id, row)
    if (!nodes.has(row.id)) {
      nodes.set(row.id, { data: { id: row.id, label: row.text } })
    }
  })

  return {
    ...graph,
    nodes: Array.from(nodes.values()),
    table_rows: Array.from(rows.values()),
  }
}

export function isolateMemoryGraph(
  graph: MemoryGraphPayload,
  matchingRows: MemoryRow[],
  totalUnits = matchingRows.length,
  limit = 500,
): MemoryGraphPayload {
  const rows = Array.from(new Map(matchingRows.map((row) => [row.id, row])).values()).slice(0, limit)
  const nodeIds = new Set(rows.map((row) => row.id))
  const graphNodes = new Map(
    graph.nodes
      .filter((node) => nodeIds.has(node.data.id))
      .map((node) => [node.data.id, node]),
  )

  rows.forEach((row) => {
    if (!graphNodes.has(row.id)) {
      graphNodes.set(row.id, { data: { id: row.id, label: row.text } })
    }
  })

  const edges = graph.edges.filter(({ data }) => nodeIds.has(data.source) && nodeIds.has(data.target))

  return {
    ...graph,
    nodes: Array.from(graphNodes.values()),
    edges,
    table_rows: rows,
    total_units: Math.max(totalUnits, rows.length),
    total_links: edges.length,
    limit,
    truncated: totalUnits > rows.length || matchingRows.length > rows.length,
  }
}

export function isolateMemoryGraphByDocuments(
  graph: MemoryGraphPayload,
  documentReferences: string[],
): MemoryGraphPayload {
  const references = new Set(documentReferences)
  const matchingRows = graph.table_rows.filter((row) => row.document_id && references.has(row.document_id))
  return isolateMemoryGraph(graph, matchingRows, matchingRows.length, graph.limit)
}

function edgeKey(edge: MemoryGraphPayload['edges'][number]): string {
  const { source, target, linkType, entityName } = edge.data
  return `${source}\u0000${target}\u0000${linkType || ''}\u0000${entityName || ''}`
}

export function rowTimestamp(row: MemoryRow, field?: MemoryColorField): string | null {
  if (field) return row[field] || null
  return row.mentioned_at || row.occurred_start || row.created_at || row.date || null
}

export function timestampValue(row?: MemoryRow, field?: MemoryColorField): number | null {
  if (!row) return null
  const value = rowTimestamp(row, field)
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

export function formatMemoryDate(value?: string | null, includeTime = true): string {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', includeTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'medium' }).format(parsed)
}

export function splitEntities(value?: string | null): string[] {
  if (!value) return []
  return value.split(',').map((entity) => entity.trim()).filter(Boolean)
}

export function displayDocumentName(value: string): string {
  return value.replace(/\.(?:md|markdown)$/i, '')
}

export function documentPreview(value?: string | null, limit = 150): string {
  const normalized = stripMarkdown(value || '').replace(/\s+/g, ' ').trim()
  if (!normalized) return ''
  return normalized.length > limit ? `${normalized.slice(0, limit).trimEnd()} ...` : normalized
}

function stripMarkdown(value: string): string {
  return value
    .replace(/```(?:[^\n]*)\n?([\s\S]*?)```/g, '$1')
    .replace(/^\s*\|?\s*:?-{3,}:?(?:\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/gm, ' ')
    .replace(/^\s*(?:[-*_]\s*){3,}$/gm, ' ')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}(?:#{1,6}|>|[-+*]|\d+[.)])\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(~~)(.*?)\1/g, '$2')
    .replace(/\|/g, ' ')
    .replace(/[*_~`]/g, '')
    .replace(/<[^>]+>/g, ' ')
}

export function hashString(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}
